import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { overtimeService } from "../../api/overtimeService";
import {
  describeRoute,
  getRouteFor,
  useMyApprovers,
} from "../approvers/useMyApprovers";
import Badge from "../../components/ui/Badge";
import Modal from "../../components/ui/Modal";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import {
  buildManilaIso,
  formatManilaTime,
  todayManilaKey,
} from "../../utils/manilaTime";
import { cx } from "../../utils/classNames";
import {
  countedOvertimeMinutes,
  endsNextDay,
  formatMinutes,
  LATE_FILING_TEXT,
  latestFileableDate,
  minutesBetweenTimes,
  OVERTIME_KEY,
  OVERTIME_RULE_TEXT,
  OVERTIME_STATUS,
  startHasPassed,
  suggestPlannedTimes,
} from "./overtimeUtils";
import ui from "../../components/ui/ui.module.css";

const MAX_REASON = 1000;

const ApproverLine = () => {
  const query = useMyApprovers();
  if (query.isLoading || query.isError) return null;

  return (
    <div className={ui.hint}>
      <i className="bi bi-person-check me-1"></i>
      Your approver: {describeRoute(getRouteFor(query.data, "OVERTIME"))}
    </div>
  );
};

// File overtime BEFORE it is worked (the server refuses a start in the past;
// HR files late ones). Today, start/end default to when the regular 8 hours
// end (from GET /overtime/preview) until the user edits them.
const FileOvertimeModal = ({ initialDate, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const todayKey = todayManilaKey();

  const [date, setDate] = useState(
    initialDate && initialDate > todayKey ? initialDate : todayKey,
  );
  const [start, setStart] = useState(null); // null = use the suggestion
  const [end, setEnd] = useState(null);
  const [reason, setReason] = useState("");

  const previewQuery = useQuery({
    queryKey: [OVERTIME_KEY, "preview", date],
    queryFn: () => overtimeService.getPreview(date),
    enabled: !!date,
  });
  const preview = previewQuery.data;
  const suggestion = suggestPlannedTimes(preview, date);

  const startValue = start ?? suggestion.start;
  const endValue = end ?? suggestion.end;
  const minutes = minutesBetweenTimes(startValue, endValue);
  const counted = countedOvertimeMinutes(minutes);
  const nextDay = endsNextDay(startValue, endValue);
  const passed = startHasPassed(date, startValue);

  const mutation = useMutation({
    mutationFn: () =>
      overtimeService.create({
        date,
        startTime: buildManilaIso(date, startValue),
        endTime: buildManilaIso(date, endValue, nextDay),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success("Overtime request sent to your approver.");
      queryClient.invalidateQueries({ queryKey: [OVERTIME_KEY] });
      onClose();
    },
  });

  const changeDate = (value) => {
    setDate(value);
    // New day, new suggestion.
    setStart(null);
    setEnd(null);
  };

  const canSubmit =
    !!date &&
    !!startValue &&
    !!endValue &&
    counted > 0 &&
    !!reason.trim() &&
    !passed &&
    !preview?.lockedReason;

  return (
    <Modal
      as="form"
      title="File overtime"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      footer={
        <>
          <button
            type="button"
            className={cx(ui.button, ui.secondary)}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={cx(ui.button, ui.primary)}
            disabled={!canSubmit || mutation.isPending}
          >
            {mutation.isPending ? "Sending…" : "Send request"}
          </button>
        </>
      }
    >
      <div className={ui.formStack}>
        <div className={cx(ui.alert, ui.alertInfo)}>
          <span>{OVERTIME_RULE_TEXT}</span>
        </div>

        <ApproverLine />

        <div>
          <label htmlFor="ot-date" className={ui.label}>
            Date
          </label>
          <input
            id="ot-date"
            type="date"
            className={ui.input}
            value={date}
            min={todayKey}
            max={latestFileableDate(todayKey)}
            onChange={(e) => changeDate(e.target.value)}
            required
          />
        </div>

        {previewQuery.isLoading ? (
          <span className={ui.hint}>Checking your hours…</span>
        ) : previewQuery.isError ? (
          <div className={cx(ui.alert, ui.alertDanger)}>
            {getErrorMessage(
              previewQuery.error,
              "Couldn't load your hours for this day.",
            )}
          </div>
        ) : (
          preview && (
            <>
              {preview.lockedReason ? (
                <div className={cx(ui.alert, ui.alertGray)}>
                  <span>
                    <i className="bi bi-lock me-2"></i>
                    {preview.lockedReason}. Overtime can no longer be filed for
                    this day.
                  </span>
                </div>
              ) : preview.workedMinutes > 0 ? (
                <div
                  className={cx(
                    ui.alert,
                    preview.beyondRegularMinutes > 0
                      ? ui.alertSuccess
                      : ui.alertGray,
                  )}
                >
                  <span>
                    You worked {formatMinutes(preview.workedMinutes)} on this
                    day
                    {preview.beyondRegularMinutes > 0
                      ? `, ${formatMinutes(preview.beyondRegularMinutes)} beyond regular hours.`
                      : ", not beyond regular hours."}
                    {preview.regularEndsAt &&
                      ` Regular hours end at ${formatManilaTime(preview.regularEndsAt)}.`}
                    {preview.lastCheckOut &&
                      ` Last check-out ${formatManilaTime(preview.lastCheckOut)}.`}
                  </span>
                </div>
              ) : null}

              {preview.requests?.length > 0 && (
                <div className={ui.hint}>
                  Already filed for this day:{" "}
                  {preview.requests.map((r) => (
                    <span key={r.id} className="me-2">
                      {formatManilaTime(r.startTime)}–
                      {formatManilaTime(r.endTime)}{" "}
                      <Badge tone={OVERTIME_STATUS[r.status]?.tone ?? "gray"}>
                        {OVERTIME_STATUS[r.status]?.label ?? r.status}
                      </Badge>
                    </span>
                  ))}
                </div>
              )}
            </>
          )
        )}

        <div className={ui.formRow}>
          <div>
            <label htmlFor="ot-start" className={ui.label}>
              Start
            </label>
            <input
              id="ot-start"
              type="time"
              className={ui.input}
              value={startValue}
              onChange={(e) => setStart(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="ot-end" className={ui.label}>
              End
            </label>
            <input
              id="ot-end"
              type="time"
              className={ui.input}
              value={endValue}
              onChange={(e) => setEnd(e.target.value)}
              required
            />
          </div>
        </div>

        {passed && (
          <div className={cx(ui.alert, ui.alertDanger)}>
            <span>
              <i className="bi bi-clock-history me-2"></i>
              This start time has already passed. {LATE_FILING_TEXT}
            </span>
          </div>
        )}

        {startValue && endValue && (
          <span className={cx(ui.hint, counted === 0 && "text-danger")}>
            {formatMinutes(minutes)}
            {nextDay && " (ends the next day)"}
            {" · "}
            {counted === 0
              ? "under 30 minutes is not overtime"
              : `counts as ${formatMinutes(counted)}`}
          </span>
        )}

        <div>
          <label htmlFor="ot-reason" className={ui.label}>
            Reason
          </label>
          <textarea
            id="ot-reason"
            rows={3}
            maxLength={MAX_REASON}
            className={cx(ui.input, ui.textarea)}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Month-end closing"
            required
          />
          <small className={ui.hint}>
            {reason.length}/{MAX_REASON}
          </small>
        </div>

        {mutation.error && (
          <div className={cx(ui.alert, ui.alertDanger)}>
            {getErrorMessage(mutation.error, "Unable to file overtime.")}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FileOvertimeModal;
